<script setup>
import { computed } from 'vue'

import { getI18n } from '@/plugins/i18n'

import { formatShortDate, getInitials } from '@/lib/utils'

import { useChips } from '@/composables/chips'
import { expenseClaimLastStepDate, formatExpenseMoney, useExpenseClaimActions } from '@/composables/expense-claim'
import { reportPeriodShortLabel } from '@/composables/report'

import AvatarImage from '@/components/shared/AvatarImage.vue'
import ListItem from '@/components/shared/ListItem.vue'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  showStaff: {
    type: Boolean,
    default: false,
  },
  showStatus: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click'])

const i18n = getI18n()
const { getExpenseClaimStatusChips, getProjectsSummaryChips, mineChip } = useChips()
const { isOwn } = useExpenseClaimActions()

const hasStaff = computed(() => props.showStaff && !!props.item.staff)
const caption = computed(() => (props.item.is_unclaimed ? i18n.t('common.expenseClaimRowsCount', { count: props.item.rows_count }, props.item.rows_count) : props.item.number))

const captionChips = computed(() => [
  ...(hasStaff.value && isOwn(props.item) ? [mineChip] : []),
  ...getProjectsSummaryChips(props.item.projects),
])
</script>

<template>
  <q-item>
    <list-item
      class="q-pa-sm"
      :avatar-icon="props.item.is_unclaimed ? 'mdi-tray-full' : 'mdi-receipt-text-outline'"
      :avatar-icon-color="props.item.is_unclaimed ? 'warning' : 'primary'"
      :label="props.item.is_unclaimed ? undefined : reportPeriodShortLabel(props.item)"
      :caption-chips="captionChips"
      :right-chips="props.showStatus ? getExpenseClaimStatusChips(props.item) : undefined"
      :right-label="formatExpenseMoney(props.item.total_reference)"
      dense
      clickable
      @click="emit('click', props.item)"
    >
      <template v-if="hasStaff" #avatar>
        <avatar-image size="md" :image="props.item.staff_avatar ?? ''" :initials="getInitials(props.item.staff_label)" />
      </template>
      <template v-if="props.item.is_unclaimed" #label>
        <q-item-label class="text-left text-weight-bold">{{ props.item.number }}</q-item-label>
      </template>
      <template #caption>
        <div v-if="hasStaff" class="ellipsis">{{ props.item.staff_label }}</div>
        <div class="ellipsis">{{ caption }}</div>
      </template>
      <template v-if="!props.item.is_unclaimed" #right-caption>
        <q-icon name="mdi-calendar" class="q-mr-xs" />
        <span>{{ formatShortDate(expenseClaimLastStepDate(props.item)) }}</span>
      </template>
    </list-item>
  </q-item>
</template>
